/**
 * Release 2 S7b — board retention: inherit (no own policy), off here (`enabled: false` stops inheritance) or own
 * rules. Any matching rule archives the asset nightly (never deletes). "Preview" asks the API what the draft would
 * archive today, by reason, before anything is saved.
 */
import { useState } from 'react';
import { Archive } from 'lucide-react';
import type { Board } from '@/lib/api/types';
import { useBoard, useBoardRetention, usePreviewBoardRetention, useSetBoardRetention, type RetentionPolicy, type RetentionPreview } from '@/lib/api/queries';
import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Spinner } from '@/components/Misc';
import { plural } from '@/lib/format';
import { toast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';

type Mode = 'inherit' | 'off' | 'custom';
type RuleKey = 'after_upload_days' | 'after_rights_expiry_days' | 'unapproved_days';
const RULES: Array<{ key: RuleKey; label: [string, string] }> = [
  { key: 'after_upload_days', label: ['Archive', 'days after upload'] },
  { key: 'after_rights_expiry_days', label: ['Archive', 'days after usage rights expire'] },
  { key: 'unapproved_days', label: ['Archive assets still without approval status', 'days after upload'] },
];
const REASON_LABEL: Record<keyof RetentionPreview['by_reason'], string> = { after_upload: 'after upload', after_rights_expiry: 'after rights expiry', unapproved: 'never reviewed' };
export const MAX_RETENTION_DAYS = 36500;

type Draft = { mode: Mode; rules: Record<RuleKey, string> };

function toDraft(policy: RetentionPolicy | null | undefined): Draft {
  const rules = { after_upload_days: '', after_rights_expiry_days: '', unapproved_days: '' };
  if (!policy) return { mode: 'inherit', rules };
  for (const r of RULES) rules[r.key] = policy[r.key] != null ? String(policy[r.key]) : '';
  return { mode: policy.enabled ? 'custom' : 'off', rules };
}

/** Draft → request policy (`null` = inherit) or an error message. */
export function draftPolicy(d: Draft): { policy: RetentionPolicy | null } | { error: string } {
  if (d.mode === 'inherit') return { policy: null };
  if (d.mode === 'off') return { policy: { enabled: false } };
  const policy: RetentionPolicy = { enabled: true, action: 'archive' };
  for (const r of RULES) {
    const raw = d.rules[r.key].trim();
    if (!raw) continue;
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1 || n > MAX_RETENTION_DAYS) return { error: `Days must be a whole number from 1 to ${MAX_RETENTION_DAYS}` };
    policy[r.key] = n;
  }
  if (RULES.every((r) => policy[r.key] == null)) return { error: 'Add at least one rule' };
  return { policy };
}

export function RetentionDialog({ board, onClose }: { board: Board; onClose: () => void }) {
  const retention = useBoardRetention(board.id);
  return (
    <Dialog open onClose={onClose} title={`Retention · ${board.name}`} width="max-w-xl">
      {retention.data ? <RetentionForm key={JSON.stringify(retention.data.policy)} board={board} saved={retention.data.policy} effective={retention.data.effective} sourceId={retention.data.source_board_id} onClose={onClose} /> : <div className="flex justify-center py-8"><Spinner /></div>}
    </Dialog>
  );
}

function RetentionForm({ board, saved, effective, sourceId, onClose }: { board: Board; saved: RetentionPolicy | null; effective: RetentionPolicy | null; sourceId: string | null; onClose: () => void }) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(saved));
  const [preview, setPreview] = useState<RetentionPreview | null>(null);
  const set = useSetBoardRetention(board.id);
  const run = usePreviewBoardRetention(board.id);
  const source = useBoard(sourceId && sourceId !== board.id ? sourceId : undefined);
  const parsed = draftPolicy(draft);
  const update = (d: Draft) => {
    setDraft(d);
    setPreview(null);
  };
  const fail = (title: string) => (e: unknown) => toast({ kind: 'error', title: isApiError(e) ? e.message : title });
  const inherited = !saved && effective ? `Inherited from ${source.data?.name ?? 'a parent board'}: ${describe(effective)}` : null;
  return (
    <div className="space-y-4 text-sm" data-retention>
      <p className="text-xs text-fg-subtle">
        Applies to assets whose home board is this board or a sub-board without its own policy. Matching assets are archived each night (never deleted); archived assets can be restored.
      </p>
      <p className="rounded-md bg-bg-sunken px-2 py-1.5 text-xs" data-retention-effective>
        {effective ? (saved ? `In effect here: ${describe(effective)}` : inherited) : 'No retention applies to this board.'}
      </p>
      <fieldset className="space-y-1.5">
        <legend className="mb-1 text-xs font-semibold tracking-wide text-fg-subtle uppercase">Policy</legend>
        {(
          [
            ['inherit', 'Inherit from parent boards'],
            ['off', 'Off for this board and its sub-boards'],
            ['custom', 'Custom rules'],
          ] as const
        ).map(([mode, label]) => (
          <label key={mode} className="flex items-center gap-2">
            <input type="radio" name="retention-mode" checked={draft.mode === mode} onChange={() => update({ ...draft, mode })} /> {label}
          </label>
        ))}
      </fieldset>
      {draft.mode === 'custom' && (
        <div className="space-y-1.5 pl-5" data-retention-rules>
          {RULES.map((r) => (
            <label key={r.key} className="flex flex-wrap items-center gap-2 text-xs">
              {r.label[0]}
              <Input type="number" min={1} max={MAX_RETENTION_DAYS} value={draft.rules[r.key]} onChange={(e) => update({ ...draft, rules: { ...draft.rules, [r.key]: e.target.value } })} className="h-7 w-20 text-xs" aria-label={`${r.label[0]} … ${r.label[1]}`} data-retention-rule={r.key} />
              {r.label[1]}
            </label>
          ))}
          <p className="text-[11px] text-fg-subtle">Leave a rule empty to skip it. Any matching rule archives the asset.</p>
        </div>
      )}
      {'error' in parsed && <p className="text-xs text-danger" role="alert">{parsed.error}</p>}
      <section className="rounded-md border border-line p-2" aria-live="polite" data-retention-preview>
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={'error' in parsed || run.isPending} onClick={() => 'policy' in parsed && run.mutate(parsed.policy, { onSuccess: setPreview, onError: fail('Could not preview retention') })} data-retention-preview-run>
            <Archive size={13} /> Preview
          </Button>
          {run.isPending && <Spinner size={14} />}
          {preview && (
            <span className="text-xs" data-retention-count={preview.count}>
              {preview.count === 0 ? 'Nothing would be archived today.' : `${plural(preview.count, 'asset', 'assets')} would be archived tonight`}
            </span>
          )}
        </div>
        {preview && preview.count > 0 && (
          <ul className="mt-1.5 flex flex-wrap gap-3 text-xs text-fg-muted">
            {(Object.keys(REASON_LABEL) as Array<keyof RetentionPreview['by_reason']>).filter((k) => preview.by_reason[k] > 0).map((k) => (
              <li key={k} data-retention-reason={k}>{preview.by_reason[k].toLocaleString()} {REASON_LABEL[k]}</li>
            ))}
          </ul>
        )}
      </section>
      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={'error' in parsed || set.isPending} onClick={() => 'policy' in parsed && set.mutate(parsed.policy, { onSuccess: () => { toast({ kind: 'success', title: 'Retention saved' }); onClose(); }, onError: fail('Could not save retention') })} data-retention-save>
          Save
        </Button>
      </div>
    </div>
  );
}

function describe(p: RetentionPolicy): string {
  const parts: string[] = [];
  if (p.after_upload_days != null) parts.push(`${plural(p.after_upload_days, 'day', 'days')} after upload`);
  if (p.after_rights_expiry_days != null) parts.push(`${plural(p.after_rights_expiry_days, 'day', 'days')} after rights expiry`);
  if (p.unapproved_days != null) parts.push(`unreviewed after ${plural(p.unapproved_days, 'day', 'days')}`);
  return `archive ${parts.join(', or ')}`;
}
